import { useEffect, useState } from "react";
import { getJob } from "../api";
import type { JobResult } from "../types";

type Props = { jobId: string; label: string };

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000; // give up after 15 minutes

export function JobStatus({ jobId, label }: Props) {
  const [result, setResult] = useState<JobResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;
    const start = Date.now();

    async function tick() {
      if (stopped) return;
      try {
        const r = await getJob(jobId);
        setResult(r);
        if (r.status === "pending" && Date.now() - start < POLL_TIMEOUT_MS) {
          setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (e) {
        setError(String(e));
      }
    }
    tick();

    return () => {
      stopped = true;
    };
  }, [jobId]);

  if (error) {
    return (
      <div className="card status-error">
        <div>{label}</div>
        <div>{error}</div>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="card muted">
        {label} <span className="status-pending">· loading…</span>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <strong>{label}</strong>
        <span className={`status-${result.status}`}>
          {result.status}
          {result.attempts != null && result.status === "ok" ? ` · ${result.attempts} attempt(s)` : ""}
        </span>
      </div>
      {result.error && <div className="status-error" style={{ marginTop: 4 }}>{result.error}</div>}
      <details style={{ marginTop: 8 }}>
        <summary className="muted">raw result</summary>
        <pre>{JSON.stringify(result, null, 2)}</pre>
      </details>
    </div>
  );
}
